import cloneDeep from 'lodash.clonedeep';

export function useSurfaceEditorStoreResize({surfaces, pauseUndo, resumeUndo, commitUndo, positionGuidesEnabled}) {

  const surfaceEditorUtils = useSurfaceEditorUtils();

  const isDragging = ref(false);
  const isRotating = ref(false);

  /**
   * Rotates a vector by a given angle
   * Used for coordinate space transformations when rotating elements
   */
  function rotateVector(dx, dy, angleDegrees) {
    if (!angleDegrees) return {dx, dy};
    const angleRad = -angleDegrees * (Math.PI / 180);
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    return {
      dx: dx * cos - dy * sin,
      dy: dx * sin + dy * cos
    };
  }

  /**
   * Calculate new element position to keep an anchor point visually fixed after dimension changes.
   * CSS rotation happens around the element's center, so changing dimensions shifts
   * the center and causes visual movement. This compensates for that shift.
   *
   * @param anchorX - anchor point X in normalized coords (0 = left, 1 = right)
   * @param anchorY - anchor point Y in normalized coords (0 = top, 1 = bottom)
   */
  function calculatePositionForFixedAnchor({
    originalX, originalY, originalWidth, originalHeight,
    newWidth, newHeight,
    anchorX, anchorY,
    rotation
  }) {
    if (!rotation) {
      return {
        x: originalX + anchorX * originalWidth - anchorX * newWidth,
        y: originalY + anchorY * originalHeight - anchorY * newHeight
      };
    }

    const r = rotation * Math.PI / 180;
    const cos = Math.cos(r);
    const sin = Math.sin(r);

    // Original anchor screen position (relative to rotation center)
    const origAnchorRelX = (anchorX - 0.5) * originalWidth;
    const origAnchorRelY = (anchorY - 0.5) * originalHeight;
    const origAnchorRotX = origAnchorRelX * cos - origAnchorRelY * sin;
    const origAnchorRotY = origAnchorRelX * sin + origAnchorRelY * cos;

    const origCenterX = originalX + originalWidth / 2;
    const origCenterY = originalY + originalHeight / 2;
    const anchorScreenX = origCenterX + origAnchorRotX;
    const anchorScreenY = origCenterY + origAnchorRotY;

    // New anchor position relative to new center
    const newAnchorRelX = (anchorX - 0.5) * newWidth;
    const newAnchorRelY = (anchorY - 0.5) * newHeight;
    const newAnchorRotX = newAnchorRelX * cos - newAnchorRelY * sin;
    const newAnchorRotY = newAnchorRelX * sin + newAnchorRelY * cos;

    // Solve: newX + newWidth/2 + newAnchorRotX = anchorScreenX
    return {
      x: anchorScreenX - newWidth / 2 - newAnchorRotX,
      y: anchorScreenY - newHeight / 2 - newAnchorRotY
    };
  }

  /**
   * Constrains drag deltas to ensure element dimensions don't go below minimum
   * Returns bounded dx and dy values
   */
  function constrainResizeDeltas({original, dragHandle, dx, dy, isFreeformResize}) {
    const minDim = SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
    const rotation = original.rotation || 0;

    // Convert screen-space deltas to local space for constraint checking
    const localDeltas = rotateVector(dx, dy, rotation);
    let boundedLocalDx = localDeltas.dx;
    let boundedLocalDy = localDeltas.dy;

    // Calculate max allowed deltas based on original dimensions and minimum size
    const maxDxShrinkFromLeft = original.width - minDim;   // max dx when shrinking from left (dx positive)
    const maxDxShrinkFromRight = -(original.width - minDim); // max dx when shrinking from right (dx negative)
    const maxDyShrinkFromTop = original.height - minDim;   // max dy when shrinking from top (dy positive)
    const maxDyShrinkFromBottom = -(original.height - minDim); // max dy when shrinking from bottom (dy negative)

    switch (dragHandle) {
      case SURFACE_EDITOR_DRAG_HANDLE.topLeft:
        if (isFreeformResize) {
          // width = original.width - localDx, height = original.height - localDy
          boundedLocalDx = Math.min(localDeltas.dx, maxDxShrinkFromLeft);
          boundedLocalDy = Math.min(localDeltas.dy, maxDyShrinkFromTop);
        } else {
          // Proportional resize - bound based on the more constrained dimension
          const scaleX = (original.width - localDeltas.dx) / original.width;
          const scaleY = (original.height - localDeltas.dy) / original.height;
          const minScale = minDim / Math.min(original.width, original.height);

          if (scaleX < minScale || scaleY < minScale) {
            // Use minScale directly to avoid jumpy behavior
            boundedLocalDx = original.width - (original.width * minScale);
            boundedLocalDy = original.height - (original.height * minScale);
          }
        }
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.topRight:
        if (isFreeformResize) {
          // width = original.width + localDx, height = original.height - localDy
          boundedLocalDx = Math.max(localDeltas.dx, maxDxShrinkFromRight);
          boundedLocalDy = Math.min(localDeltas.dy, maxDyShrinkFromTop);
        } else {
          const scaleX = (original.width + localDeltas.dx) / original.width;
          const scaleY = (original.height - localDeltas.dy) / original.height;
          const minScale = minDim / Math.min(original.width, original.height);

          if (scaleX < minScale || scaleY < minScale) {
            // Use minScale directly to avoid jumpy behavior
            boundedLocalDx = (original.width * minScale) - original.width;
            boundedLocalDy = original.height - (original.height * minScale);
          }
        }
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottomLeft:
        if (isFreeformResize) {
          // width = original.width - localDx, height = original.height + localDy
          boundedLocalDx = Math.min(localDeltas.dx, maxDxShrinkFromLeft);
          boundedLocalDy = Math.max(localDeltas.dy, maxDyShrinkFromBottom);
        } else {
          const scaleX = (original.width - localDeltas.dx) / original.width;
          const scaleY = (original.height + localDeltas.dy) / original.height;
          const minScale = minDim / Math.min(original.width, original.height);

          if (scaleX < minScale || scaleY < minScale) {
            // Use minScale directly to avoid jumpy behavior
            boundedLocalDx = original.width - (original.width * minScale);
            boundedLocalDy = (original.height * minScale) - original.height;
          }
        }
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottomRight:
        if (isFreeformResize) {
          // width = original.width + localDx, height = original.height + localDy
          boundedLocalDx = Math.max(localDeltas.dx, maxDxShrinkFromRight);
          boundedLocalDy = Math.max(localDeltas.dy, maxDyShrinkFromBottom);
        } else {
          const scaleX = (original.width + localDeltas.dx) / original.width;
          const scaleY = (original.height + localDeltas.dy) / original.height;
          const minScale = minDim / Math.min(original.width, original.height);

          if (scaleX < minScale || scaleY < minScale) {
            // Use minScale directly to avoid jumpy behavior
            boundedLocalDx = (original.width * minScale) - original.width;
            boundedLocalDy = (original.height * minScale) - original.height;
          }
        }
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.left:
        // width = original.width - localDx
        boundedLocalDx = Math.min(localDeltas.dx, maxDxShrinkFromLeft);
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.right:
        // width = original.width + localDx
        boundedLocalDx = Math.max(localDeltas.dx, maxDxShrinkFromRight);
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.top:
        // height = original.height - localDy
        boundedLocalDy = Math.min(localDeltas.dy, maxDyShrinkFromTop);
        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottom:
        // height = original.height + localDy
        boundedLocalDy = Math.max(localDeltas.dy, maxDyShrinkFromBottom);
        break;
    }

    // Convert bounded local-space deltas back to screen space
    const boundedScreenDeltas = rotateVector(boundedLocalDx, boundedLocalDy, -rotation);
    return {dx: boundedScreenDeltas.dx, dy: boundedScreenDeltas.dy};
  }

  function dragElement({element, dragHandle, dx, dy, finalize, isSnapCorrection}) {
    const context = surfaceEditorUtils.findElementInSurface({
      elementId: element.id,
      surfaces: surfaces.value
    });
    if (!context) {
      isDragging.value = false;
      resumeUndo();
      return;
    }
    const {element: undoableElement, surface} = context;
    const elementPositionGuides = useElementPositionGuides({surface});

    if (finalize) {
      isDragging.value = false;
      delete undoableElement._original;
      elementPositionGuides.clear();

      resumeUndo();
      commitUndo();

      return;
    }

    if (!isSnapCorrection) {
      pauseUndo();
    }
    isDragging.value = true;

    undoableElement._original = undoableElement._original || {
      x: undoableElement.x,
      y: undoableElement.y,
      width: undoableElement.width,
      height: undoableElement.height,
      rotation: undoableElement.rotation || 0,
      aspect: undoableElement.width / undoableElement.height,
      content: cloneDeep(undoableElement.content)
    };
    const workingElement = cloneDeep(undoableElement);

    let dominantScale;
    const isFreeformResize = SURFACE_EDITOR_FREEFORM_RESIZE_ELEMENT_TYPES.includes(workingElement.type);

    // Apply minimum dimension constraints to prevent elements from becoming too small
    const constrained = constrainResizeDeltas({
      original: workingElement._original,
      dragHandle,
      dx,
      dy,
      isFreeformResize
    });
    dx = constrained.dx;
    dy = constrained.dy;

    //todo: modifier keys drag for images: 1) shift + drag = crop; 2) alt + drag = resize from center
    switch (dragHandle) {
      case SURFACE_EDITOR_DRAG_HANDLE.all:
        workingElement.x = Math.round(workingElement._original.x + dx);
        workingElement.y = Math.round(workingElement._original.y + dy);

        break;
      case SURFACE_EDITOR_DRAG_HANDLE.topLeft: {
        // Anchor: bottom-right (1, 1)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        if (isFreeformResize) {
          workingElement.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.width - localDeltas.dx));
          workingElement.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.height - localDeltas.dy));
        } else {
          const origDiagonal = Math.sqrt(workingElement._original.width ** 2 + workingElement._original.height ** 2);
          const newDiagonal = Math.sqrt((workingElement._original.width - localDeltas.dx) ** 2 + (workingElement._original.height - localDeltas.dy) ** 2);
          dominantScale = newDiagonal / origDiagonal;
          workingElement.width = Math.round(workingElement._original.width * dominantScale);
          workingElement.height = Math.round(workingElement._original.height * dominantScale);
        }

        const posTL = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 1, anchorY: 1, rotation
        });
        workingElement.x = Math.round(posTL.x);
        workingElement.y = Math.round(posTL.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.topRight: {
        // Anchor: bottom-left (0, 1)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        if (isFreeformResize) {
          workingElement.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.width + localDeltas.dx));
          workingElement.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.height - localDeltas.dy));
        } else {
          const origDiagonal = Math.sqrt(workingElement._original.width ** 2 + workingElement._original.height ** 2);
          const newDiagonal = Math.sqrt((workingElement._original.width + localDeltas.dx) ** 2 + (workingElement._original.height - localDeltas.dy) ** 2);
          dominantScale = newDiagonal / origDiagonal;
          workingElement.width = Math.round(workingElement._original.width * dominantScale);
          workingElement.height = Math.round(workingElement._original.height * dominantScale);
        }

        const posTR = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 0, anchorY: 1, rotation
        });
        workingElement.x = Math.round(posTR.x);
        workingElement.y = Math.round(posTR.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.bottomLeft: {
        // Anchor: top-right (1, 0)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        if (isFreeformResize) {
          workingElement.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.width - localDeltas.dx));
          workingElement.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.height + localDeltas.dy));
        } else {
          const origDiagonal = Math.sqrt(workingElement._original.width ** 2 + workingElement._original.height ** 2);
          const newDiagonal = Math.sqrt((workingElement._original.width - localDeltas.dx) ** 2 + (workingElement._original.height + localDeltas.dy) ** 2);
          dominantScale = newDiagonal / origDiagonal;
          workingElement.width = Math.round(workingElement._original.width * dominantScale);
          workingElement.height = Math.round(workingElement._original.height * dominantScale);
        }

        const posBL = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 1, anchorY: 0, rotation
        });
        workingElement.x = Math.round(posBL.x);
        workingElement.y = Math.round(posBL.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.bottomRight: {
        // Anchor: top-left (0, 0)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        if (isFreeformResize) {
          workingElement.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.width + localDeltas.dx));
          workingElement.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.height + localDeltas.dy));
        } else {
          const origDiagonal = Math.sqrt(workingElement._original.width ** 2 + workingElement._original.height ** 2);
          const newDiagonal = Math.sqrt((workingElement._original.width + localDeltas.dx) ** 2 + (workingElement._original.height + localDeltas.dy) ** 2);
          dominantScale = newDiagonal / origDiagonal;
          workingElement.width = Math.round(workingElement._original.width * dominantScale);
          workingElement.height = Math.round(workingElement._original.height * dominantScale);
        }

        const posBR = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 0, anchorY: 0, rotation
        });
        workingElement.x = Math.round(posBR.x);
        workingElement.y = Math.round(posBR.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.left: {
        // Anchor: right edge midpoint (1, 0.5)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        workingElement.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.width - localDeltas.dx));

        const posL = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 1, anchorY: 0.5, rotation
        });
        workingElement.x = Math.round(posL.x);
        workingElement.y = Math.round(posL.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.bottom: {
        // Anchor: top edge midpoint (0.5, 0)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        workingElement.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.height + localDeltas.dy));

        const posB = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 0.5, anchorY: 0, rotation
        });
        workingElement.x = Math.round(posB.x);
        workingElement.y = Math.round(posB.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.right: {
        // Anchor: left edge midpoint (0, 0.5)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        workingElement.width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.width + localDeltas.dx));

        const posR = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 0, anchorY: 0.5, rotation
        });
        workingElement.x = Math.round(posR.x);
        workingElement.y = Math.round(posR.y);

        break;
      }
      case SURFACE_EDITOR_DRAG_HANDLE.top: {
        // Anchor: bottom edge midpoint (0.5, 1)
        const rotation = workingElement._original.rotation || 0;
        const localDeltas = rotateVector(dx, dy, rotation);

        workingElement.height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(workingElement._original.height - localDeltas.dy));

        const posT = calculatePositionForFixedAnchor({
          originalX: workingElement._original.x, originalY: workingElement._original.y,
          originalWidth: workingElement._original.width, originalHeight: workingElement._original.height,
          newWidth: workingElement.width, newHeight: workingElement.height,
          anchorX: 0.5, anchorY: 1, rotation
        });
        workingElement.x = Math.round(posT.x);
        workingElement.y = Math.round(posT.y);

        break;
      }
    }

    // Apply snap-to-guide alignment
    if (positionGuidesEnabled.value && !isSnapCorrection) {
      const {snapDx, snapDy} = elementPositionGuides.setGuides({
        element: workingElement,
        surface,
        dragHandle,
        positionGuidesEnabled,
        alignMiddle: !workingElement.rotation || dragHandle === SURFACE_EDITOR_DRAG_HANDLE.all
      });

      if (snapDx || snapDy) {
        // Recursively call with corrected deltas
        return dragElement({
          element,
          dragHandle,
          dx: dx + snapDx,
          dy: dy + snapDy,
          isSnapCorrection: true
        });
      }
    } else if (positionGuidesEnabled.value && isSnapCorrection) {
      // Update guides for display (no further snapping)
      elementPositionGuides.setGuides({
        element: workingElement,
        surface,
        dragHandle,
        positionGuidesEnabled,
        alignMiddle: !workingElement.rotation || dragHandle === SURFACE_EDITOR_DRAG_HANDLE.all
      });
    }

    switch (workingElement.type) {
      case SURFACE_EDITOR_ELEMENT_TYPE.image:
        morphImage({element: workingElement, dragHandle, dx, dy});
        break;

      case SURFACE_EDITOR_ELEMENT_TYPE.text:
        morphText({element: workingElement, dragHandle, dx, dy});
        break;
    }

    Object.assign(undoableElement, workingElement);
  }

  function rotateElement({ element, dx, dy, finalize, handleDistanceFromCenter }) {
    const context = surfaceEditorUtils.findElementInSurface({
      elementId: element.id,
      surfaces: surfaces.value
    });
    if (!context) {
      isRotating.value = false;
      resumeUndo();
      return;
    }
    const {element: undoableElement} = context;

    if (finalize) {
      isRotating.value = false;
      delete undoableElement._original;

      resumeUndo();
      commitUndo();
      return;
    }

    pauseUndo();

    undoableElement._original = undoableElement._original || {
      rotation: undoableElement.rotation || 0,
      width: undoableElement.width,
      height: undoableElement.height
    };

    const workingElement = cloneDeep(undoableElement);
    isRotating.value = true;

    const currentRotation = workingElement._original.rotation || 0;

    // Transform screen-space deltas to element-local space
    const localDeltas = rotateVector(dx, dy, currentRotation);

    const { x: handleX, y: handleY } = handleDistanceFromCenter;

    // Initial vector: from center → handle (local space)
    const startVector = {
      x: handleX,
      y: handleY
    };

    // Current vector: initial handle position + local drag deltas
    const currentVector = {
      x: handleX + localDeltas.dx,
      y: handleY + localDeltas.dy
    };

    // Measure angle from positive Y axis, clockwise-positive
    const startAngle =
      Math.atan2(startVector.x, startVector.y) * (180 / Math.PI);

    const currentAngle =
      Math.atan2(currentVector.x, currentVector.y) * (180 / Math.PI);

    let newRotation = Math.round(
      workingElement._original.rotation - (currentAngle - startAngle)
    );

    // Snap logic
    const nearestSnapAngle =
      Math.round(newRotation / SURFACE_EDITOR_ROTATION_SNAP_INTERVAL) *
      SURFACE_EDITOR_ROTATION_SNAP_INTERVAL;

    const distanceToSnap = Math.abs(newRotation - nearestSnapAngle);

    if (distanceToSnap <= SURFACE_EDITOR_ROTATION_SNAP_THRESHOLD) {
      newRotation = nearestSnapAngle;
    }

    workingElement.rotation = newRotation;
    Object.assign(undoableElement, workingElement);
  }

  function cancelActiveInteraction({surface} = {}) {
    isDragging.value = false;
    isRotating.value = false;

    if (surface) {
      useElementPositionGuides({surface}).clear();
    }

    resumeUndo();
  }

  function morphImage({element, dragHandle, dx, dy}) {
    const wScale = element.width / element._original.width;
    const hScale = element.height / element._original.height;
    let offsetScale, noOffsetWidth, noOffsetHeight;

    switch (dragHandle) {
      case SURFACE_EDITOR_DRAG_HANDLE.topLeft:
      case SURFACE_EDITOR_DRAG_HANDLE.topRight:
      case SURFACE_EDITOR_DRAG_HANDLE.bottomLeft:
      case SURFACE_EDITOR_DRAG_HANDLE.bottomRight:
        //scale
        element.content.scale = element._original.content.scale * wScale;
        element.content.offset_x = Math.round(element._original.content.offset_x * wScale);
        element.content.offset_y = Math.round(element._original.content.offset_y * hScale);

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.left:
        // Use dimension change instead of raw dx, since element.width is already
        // computed in local space (rotation-aware) by the drag handler
        const localDxLeft = element._original.width - element.width;
        const newOffsetX = element._original.content.offset_x - localDxLeft;
        element.content.offset_x = Math.round(Math.min(newOffsetX, 0));

        if (newOffsetX > 0) {
          noOffsetWidth = element.width - newOffsetX;
          offsetScale = element.width / noOffsetWidth;
          element.content.scale = element._original.content.scale * offsetScale;
          element.content.offset_y = Math.round(element._original.content.offset_y + ((element.height - (element.height * offsetScale)) / 2));
        }

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.top:
        // Use dimension change instead of raw dy, since element.height is already
        // computed in local space (rotation-aware) by the drag handler
        const localDyTop = element._original.height - element.height;
        const newOffsetY = element._original.content.offset_y - localDyTop;
        element.content.offset_y = Math.round(Math.min(newOffsetY, 0));

        if (newOffsetY > 0) {
          noOffsetHeight = element.height - newOffsetY;
          offsetScale = element.height / noOffsetHeight;
          element.content.scale = element._original.content.scale * offsetScale;
          element.content.offset_x = Math.round(element._original.content.offset_x + ((element.width - (element.width * offsetScale)) / 2));
        }

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.right:
        const dxToScale = element.width - (element._original.content.image_width * element._original.content.scale) - element._original.content.offset_x;

        if (dxToScale > 0) {
          noOffsetWidth = element.width - dxToScale;
          offsetScale = element.width / noOffsetWidth;
          element.content.scale = element._original.content.scale * offsetScale;
          element.content.offset_y = Math.round(element._original.content.offset_y + ((element.height - (element.height * offsetScale)) / 2));
          element.content.offset_x = Math.round(element.width - (element.content.image_width * element.content.scale));
        }

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottom:
        const dyToScale = element.height - (element._original.content.image_height * element._original.content.scale) - element._original.content.offset_y;

        if (dyToScale > 0) {
          noOffsetHeight = element.height - dyToScale;
          offsetScale = element.height / noOffsetHeight;
          element.content.scale = element._original.content.scale * offsetScale;
          element.content.offset_x = Math.round(element._original.content.offset_x + ((element.width - (element.width * offsetScale)) / 2));
          element.content.offset_y = Math.round(element.height - (element.content.image_height * element.content.scale));
        }

        break;
    }

  }

  function morphText({element, dragHandle, dx, dy, height}) {
    if (dragHandle) { //it's a drag
      const wScale = element.width / element._original.width;
      const hScale = element.height / element._original.height;

      switch (dragHandle) {
        case SURFACE_EDITOR_DRAG_HANDLE.topLeft:
        case SURFACE_EDITOR_DRAG_HANDLE.topRight:
        case SURFACE_EDITOR_DRAG_HANDLE.bottomLeft:
        case SURFACE_EDITOR_DRAG_HANDLE.bottomRight:
          //scale
          element.content.scale = element._original.content.scale * wScale;

          break;
        case SURFACE_EDITOR_DRAG_HANDLE.top:
        case SURFACE_EDITOR_DRAG_HANDLE.bottom:
          element.content.font_size = Math.round(element._original.content.font_size * hScale);
          element.height = element._original.height * hScale;

          const newWidth = element._original.width * hScale;
          element.width = newWidth;
          element.x = element._original.x - ((newWidth - element._original.width) / 2);

          break;
        case SURFACE_EDITOR_DRAG_HANDLE.left:
        case SURFACE_EDITOR_DRAG_HANDLE.right:

          break;
      }
    }

    if (height) { //something is trying to tweak the box size
      element.height = height;
    }
  }

  return {
    isDragging,
    isRotating,
    dragElement,
    rotateElement,
    cancelActiveInteraction,
    morphImage,
    morphText
  };
}
